import { KeenSliderInstance, KeenSliderPlugin } from 'keen-slider/react';

/**
 * Keeps the focused slide fully in view when tabbing through the carousel.
 * keen-slider positions slides with a CSS transform rather than native
 * scrolling, so the browser's built-in "scroll focused element into view"
 * behavior never kicks in on its own — this plugin re-implements it.
 */
export const createFocusPlugin = (): KeenSliderPlugin => (slider) => {
  /**
   * Finds the absolute index of the slide closest to the current position that matches the target index.
   */
  const closestAbsoluteIdx = (slider: KeenSliderInstance, targetIndex: number) => {
    const slidesLength = slider.slides.length;
    const currentAbs = slider.track.details.abs;
    const currentIndex = ((currentAbs % slidesLength) + slidesLength) % slidesLength;

    const forwardSteps = (((targetIndex - currentIndex) % slidesLength) + slidesLength) % slidesLength;
    const backwardSteps = (((currentIndex - targetIndex) % slidesLength) + slidesLength) % slidesLength;

    return forwardSteps <= backwardSteps ? currentAbs + forwardSteps : currentAbs - backwardSteps;
  };

  /**
   * Only keyboard focus should move the slider — pressing the mouse on a slide link
   * (e.g. to start dragging) also focuses it, but shouldn't snap it into view.
   */
  const handleFocusIn = (event: FocusEvent) => {
    if (!(event.target instanceof HTMLElement) || !event.target.matches(':focus-visible')) return;

    const slide = event.target.closest<HTMLElement>('.keen-slider__slide');

    if (!slide) return;

    const index = slider.slides.indexOf(slide);

    if (index === -1) return;

    const target = closestAbsoluteIdx(slider, index);

    slider.moveToIdx(target, true);
  };

  slider.on('created', () => {
    slider.container.addEventListener('focusin', handleFocusIn);
  });
};
